import { Component, DestroyRef, OnInit, Input, Output, EventEmitter, ElementRef, inject, OnChanges, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Validators, FormControl } from '@angular/forms';
import { EvaluationService, UpdateEvalPriceRequest } from '@app-services/evaluation.service';
import { debounceTime, finalize, switchMap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import {
  StatusEvaluation,
  HistoryItemType,
  EvaluationVehicle,
  EvaluatorOption,
} from '@app-model/evaluation';
import { ChangeStatusEvaluation } from '@app-components/evaluation/evaluation-vehicle-detail/evaluation-vehicle-detail.component';
import { HistoryLocationService } from '@app-services/history-location.service';
import { OpenService } from '@app-services/open.service';
import { SessionService } from '@app/store/session/session.service';
import { Role, UserInfo, LayoutVars, Permission } from '@app/store/session/session.store';
import { PricingCalculatorAppliedPrice } from '@app-model/pricing-calculator.model';
import { toPriceCentValue } from '@app-utils/eval-price.utils';
import { isFipeOutOfExpectedRange } from '@app-utils/fipe-comparison.utils';
import { formatPricingCurrency, roundHalfUp, suggestListPriceFromMargin } from '@app-utils/pricing-calculator.utils';

enum PricingFlowType {
  EXPECTED_PRICE = 'EXPECTED_PRICE',
  PURCHASE = 'PURCHASE',
}

const FIPE_ALERT_DEBOUNCE_MS = 1000;
const LIST_PRICE_MARGIN_DEBOUNCE_MS = 200;
// Fração da folga entre o preço sugerido e o valor da paga liberada para ajuste manual.
const CALCULATOR_PRICE_HEADROOM_SHARE = 0.5;

@Component({
  selector: 'app-card-offer-evaluation',
  templateUrl: './card-offer-evaluation.component.html',
  styleUrls: ['./card-offer-evaluation.component.scss'],
})
export class CardOfferEvaluationComponent implements OnInit, OnChanges {
  readonly PricingFlowType = PricingFlowType;
  readonly onlineArchiveReason = 'ONLINE_ASSESSMENT';

  @Input() vehicle: EvaluationVehicle;
  @Input() revisionEstimated = 0;
  @Input() molicarPrice = 0;
  @Input() requiredPrice = false;
  @Input() listPriceMarginPercentage: number | null = null;
  @Input() expectedPriceRounding: number | null = null;
  @Output() changeStatus = new EventEmitter<ChangeStatusEvaluation>();
  @Output() updatePrice = new EventEmitter<UpdateEvalPriceRequest>();

  obsModal: boolean;
  obsMsg: string;
  showGiveBack = false;
  loading = false;
  loadingBtn: boolean = false;
  showFooter = true;
  destination: FormControl = new FormControl(null, [Validators.required]);
  commentGiveBack: FormControl = new FormControl('', [Validators.required]);
  commentClosePricing: FormControl = new FormControl('');
  price: FormControl = new FormControl(null, [Validators.required]);
  pricingType: FormControl = new FormControl(PricingFlowType.PURCHASE);
  evaluator: FormControl = new FormControl(null);
  evaluatorOptions: EvaluatorOption[] = [];
  factoryBonus: FormControl = new FormControl(null, []);
  minSellPrice: FormControl = new FormControl(null, []);
  listPrice: FormControl = new FormControl(null, []);
  addComment: FormControl = new FormControl(false);
  fipePrice = 0;
  private oldPrice: number;
  private oldFactoryBonus: number;
  private oldListPrice: number;
  private oldMinSellPrice: number;
  private readonly TUTORIAL_KEY = 'pricing-card-tour-v2';
  showTutorial = false;
  userInfo: UserInfo;
  showFactoryBonus: boolean;
  canEditFactoryBonus: boolean;
  canEditListMinPrices: boolean;
  showListMinPrices: boolean;
  canEditPricingCard = false;
  layoutVars: LayoutVars | null = null;
  priceFilledByCalculator = false;
  fipeOutOfRangeAlertVisible = false;
  private tradeInMarketPrice: number | null = null;
  private tradeInDealerCosts: number | null = null;
  private calculatorPaymentValue: number | null = null;
  private calculatorSuggestedPrice: number | null = null;
  private pendingSuggestedPrice: PricingCalculatorAppliedPrice | null = null;
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private evaluationService: EvaluationService,
    private toastrService: ToastrService,
    private historyLocationService: HistoryLocationService,
    private sessionService: SessionService,
    private openService: OpenService,
    private elRef: ElementRef
  ) { }

  ngOnInit() {
    this.initializeForm();
    this.sessionService.getUser().subscribe(userInfo => {
      this.userInfo = userInfo;
      this.checkPermissions();
      this.loadEvaluatorOptions();
    });
    this.openService.getLayoutVars().subscribe(layoutVars => this.layoutVars = layoutVars);
    this.obsMsg = '';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.vehicle && !changes.vehicle.firstChange) {
      this.evaluator.setValue(this.vehicle.currentEvaluatorId ?? null, { emitEvent: false });
      this.checkPermissions();
      this.loadEvaluatorOptions();
    }

    if (changes.requiredPrice && !changes.requiredPrice.firstChange) {
      this.checkPermissions();
    }
  }

   initTutorial(): void {
    if (localStorage.getItem(this.TUTORIAL_KEY)) return;
    this.showTutorial = true;
  }

  closeTutorial(): void {
    this.showTutorial = false;
    localStorage.setItem(this.TUTORIAL_KEY, '1');
    document.body.style.overflow = '';
  }

  private initializeForm(): void {
    if (!this.vehicle) return;

    this.price.setValue(this.vehicle.price);
    this.factoryBonus.setValue(this.vehicle.factoryBonus);
    this.minSellPrice.setValue(this.vehicle.minSellPrice);
    this.listPrice.setValue(this.vehicle.listPrice);
    this.destination.setValue(this.vehicle.destination);
    this.evaluator.setValue(this.vehicle.currentEvaluatorId ?? null);

    this.oldPrice = this.vehicle.price;
    this.oldFactoryBonus = this.vehicle.factoryBonus;
    this.oldMinSellPrice = this.vehicle.minSellPrice;
    this.oldListPrice = this.vehicle.listPrice;
    this.fipePrice = this.vehicle.fipePrice ? this.vehicle.fipePrice : 0;
    this.tradeInMarketPrice = this.vehicle.tradeInMarketPrice > 0
      ? this.vehicle.tradeInMarketPrice
      : null;
    this.tradeInDealerCosts = this.vehicle.tradeInDealerCosts != null
      ? this.vehicle.tradeInDealerCosts
      : null;
    this.configurePricingTypeControl();

    this.price.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.priceFilledByCalculator && this.price.dirty) {
          this.priceFilledByCalculator = false;
        }
      });

    this.price.valueChanges
      .pipe(
        debounceTime(LIST_PRICE_MARGIN_DEBOUNCE_MS),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.syncListPriceFromMargin());

    this.price.valueChanges
      .pipe(
        debounceTime(FIPE_ALERT_DEBOUNCE_MS),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.syncFipeOutOfRangeAlert());
  }

  public checkPermissions(): void {
    if (!this.userInfo || !this.vehicle) return;
    const hasPermission = this.userInfo.hasAnyPermission(Permission.PRICE_EVALUATION);
    
    this.showFactoryBonus =
      this.userInfo.hasRoles('ADMIN', 'OEM_DEALER_MANAGER', 'SELLER_MANAGER')
      || (this.userInfo.hasRoles('DESK_BUY', 'DESK_SELL') && this.vehicle.factoryBonus > 0)
      || (this.userInfo.hasRole(Role.EVAL_DEALER_MANAGER) && this.userInfo.evalDealersIds.includes(this.vehicle.evalDealerId))
      || hasPermission;

    this.canEditFactoryBonus = this.userInfo.hasRoles('ADMIN', 'OEM_DEALER_MANAGER', 'SELLER_MANAGER') 
      || hasPermission;
    
    this.canEditListMinPrices = this.userInfo.hasRoles('ADMIN', 'OEM_DEALER_MANAGER', 'DESK_BUY')
      || (this.userInfo.hasRole(Role.EVAL_DEALER_MANAGER) && this.userInfo.evalDealersIds.includes(this.vehicle.evalDealerId))
      || hasPermission;

    this.showListMinPrices = this.canEditListMinPrices;

    const shouldRequirePricingFields = !!this.userInfo.requiredPricingFields;

    if (this.canEditListMinPrices && shouldRequirePricingFields) {
      this.minSellPrice.setValidators([Validators.required]);
      this.listPrice.setValidators([Validators.required]);
    } else {
      this.minSellPrice.clearValidators();
      this.listPrice.clearValidators();
    }
    this.minSellPrice.updateValueAndValidity({ emitEvent: false });
    this.listPrice.updateValueAndValidity({ emitEvent: false });

    if (!this.showFactoryBonus && this.userInfo.hasRole('SELLER') && this.vehicle.factoryBonus > 0) {
      this.vehicle.price += this.vehicle.factoryBonus;
    }

    const isResponsible = this.vehicle.pricingUserId === this.userInfo.id;
    this.canEditPricingCard = isResponsible;
    if (!isResponsible) {
      this.price.disable();
      this.factoryBonus.disable();
      this.minSellPrice.disable();
      this.listPrice.disable();
      this.commentClosePricing.disable();
      this.destination.disable();
      this.evaluator.disable();
      this.canEditFactoryBonus = false;
      this.canEditListMinPrices = false;
    } else {
      if (this.isCalculatorPriceLocked()) {
        this.price.disable();
      } else {
        this.price.enable();
      }
      this.syncCalculatorPriceValidators();
      if (this.canEditFactoryBonus) {
        this.factoryBonus.enable();
      } else {
        this.factoryBonus.disable();
      }
      if (this.canEditListMinPrices) {
        this.minSellPrice.enable();
        this.listPrice.enable();
      } else {
        this.minSellPrice.disable();
        this.listPrice.disable();
      }
      this.commentClosePricing.enable();
      this.destination.enable();
      this.evaluator.enable();

      if (this.pendingSuggestedPrice) {
        this.applySuggestedPrice(this.pendingSuggestedPrice);
      }
    }

    this.configurePricingTypeControl();
  }

  onClickGiveBack() {
    this.showGiveBack = !this.showGiveBack;
  }

  onClickSendBackToEvaluator() {
    this.loading = true;
    this.commentGiveBack.disable();
    const comments = this.commentGiveBack.value;
    const id = this.vehicle.id;
    this.evaluationService
      .sendBackToEvaluator(id, comments)
      .pipe(
        finalize(() => {
          this.commentGiveBack.enable();
          this.loading = false;
        })
      )
      .subscribe((response: any) => {
        if (response.success) {
          this.showGiveBack = false;
          this.showFooter = false;
          this.toastrService.success('Captação devolvida com sucesso', null, {
            progressBar: true,
          });
          const changeStatus: ChangeStatusEvaluation = {
            status: StatusEvaluation.RETURNED_EVAL,
            itemHistory: {
              type: HistoryItemType.RETURNED_EVAL,
              createdAt: new Date(),
              comments,
            },
            pricingUserId: null,
            pricingUserName: null,
            pricingStartedAt: null,
          };
          this.historyLocationService.goBackEvaluation();
          this.changeStatus.emit(changeStatus);
          return;
        }
      });
  }

  applySuggestedPrice(applied: PricingCalculatorAppliedPrice): void {
    this.pendingSuggestedPrice = null;
    this.tradeInMarketPrice = applied.marketValue;
    this.tradeInDealerCosts = applied.dealerCosts;
    this.calculatorPaymentValue = applied.paymentValue;
    this.calculatorSuggestedPrice = applied.suggestedPrice;
    this.syncCalculatorPriceValidators();

    if (this.isCalculatorPriceLocked()) {
      this.applySuggestedPriceToForm(applied);
      return;
    }

    if (this.isCalculatorPaymentCeilingActive()) {
      return;
    }

    if (this.price.disabled) {
      this.pendingSuggestedPrice = applied;
      return;
    }

    this.applySuggestedPriceToForm(applied);
  }

  private applySuggestedPriceToForm(applied: PricingCalculatorAppliedPrice): void {
    this.price.setValue(applied.suggestedPrice, { emitEvent: false });
    this.price.markAsPristine();
    this.priceFilledByCalculator = true;
    this.syncFipeOutOfRangeAlert();
    this.syncListPriceFromMargin();
  }

  private syncListPriceFromMargin(): void {
    if (this.listPriceMarginPercentage == null) {
      return;
    }
    if (this.listPrice.disabled) {
      return;
    }
    if (!this.shouldShowListMinPricesFields()) {
      return;
    }

    const price = Number(this.price.value);
    if (!price || price <= 0) {
      return;
    }

    const calculatorFilled = this.tradeInMarketPrice != null || this.priceFilledByCalculator;
    if (!calculatorFilled) {
      return;
    }

    const suggested = suggestListPriceFromMargin(
      price,
      this.listPriceMarginPercentage,
      this.expectedPriceRounding,
    );
    this.listPrice.setValue(suggested, { emitEvent: false });
  }

  isListPriceMarginHintVisible(): boolean {
    return this.listPriceMarginPercentage != null
      && this.shouldShowListMinPricesFields()
      && this.listPrice.enabled;
  }

  private hasPersistedTradeInMarketPrice(): boolean {
    return (this.vehicle?.tradeInMarketPrice ?? 0) > 0;
  }

  isCalculatorPriceLocked(): boolean {
    return !!this.requiredPrice
      && !this.canOverrideRequiredPrice()
      && !this.hasPersistedTradeInMarketPrice();
  }

  isCalculatorPaymentCeilingActive(): boolean {
    return !!this.requiredPrice
      && !this.canOverrideRequiredPrice()
      && this.hasPersistedTradeInMarketPrice();
  }

  calculatorPriceCeiling(): number | null {
    if (this.calculatorPaymentValue == null) {
      return null;
    }

    if (this.calculatorSuggestedPrice == null) {
      return this.calculatorPaymentValue;
    }

    const headroom = Math.max(0, this.calculatorPaymentValue - this.calculatorSuggestedPrice);
    return roundHalfUp(this.calculatorSuggestedPrice + headroom * CALCULATOR_PRICE_HEADROOM_SHARE);
  }

  calculatorPriceCeilingLabel(): string {
    const ceiling = this.calculatorPriceCeiling();
    return ceiling == null ? '' : formatPricingCurrency(ceiling);
  }

  private isPriceAboveCalculatorCeiling(value: number | null): boolean {
    if (!this.isCalculatorPaymentCeilingActive() || value == null) {
      return false;
    }

    const ceiling = this.calculatorPriceCeiling();
    return ceiling != null && value > ceiling;
  }

  private syncCalculatorPriceValidators(): void {
    const validators = [Validators.required];
    const ceiling = this.isCalculatorPaymentCeilingActive() ? this.calculatorPriceCeiling() : null;

    if (ceiling != null) {
      validators.push(Validators.max(ceiling));
    }

    this.price.setValidators(validators);
    this.price.updateValueAndValidity({ emitEvent: false });
  }

  isPriceReady(): boolean {
    const value = this.price.value;
    if (value == null || value <= 0) {
      return false;
    }
    if (this.isPriceAboveCalculatorCeiling(value)) {
      return false;
    }
    return this.price.disabled || this.price.valid;
  }

  private canOverrideRequiredPrice(): boolean {
    if (!this.userInfo) {
      return false;
    }

    return this.userInfo.hasRoles(Role.ADMIN, Role.OEM_DEALER_MANAGER)
      || this.userInfo.hasPermission(Permission.OVERRIDE_TRADE_IN_PRICING_CALCULATOR_PRICE);
  }

  isOnlineEvaluation(): boolean {
    return this.vehicle?.type === 'ONLINE';
  }

  showPricingTypeSelector(): boolean {
    return this.isOnlineEvaluation() && this.canEditPricingCard;
  }

  isExpectedPriceMode(): boolean {
    return this.pricingType.value === PricingFlowType.EXPECTED_PRICE;
  }

  shouldShowEvaluatorField(): boolean {
    return this.isOnlineEvaluation()
      && this.canEditPricingCard
      && this.pricingType.value === PricingFlowType.PURCHASE;
  }

  selectedEvaluatorHasNoCpf(): boolean {
    return this.evaluatorOptions.some(option =>
      option.id === this.evaluator.value && !option.hasCpf,
    );
  }

  selectedEvaluatorName(): string {
    return this.evaluatorOptions.find(option => option.id === this.evaluator.value)?.name ?? 'Não informar';
  }

  shouldShowArchiveWarning(): boolean {
    return this.showPricingTypeSelector() && this.isExpectedPriceMode();
  }

  onPriceBlur(): void {
    this.syncFipeOutOfRangeAlert();
  }

  private syncFipeOutOfRangeAlert(): void {
    this.fipeOutOfRangeAlertVisible = isFipeOutOfExpectedRange(
      this.price.value,
      this.fipePrice,
    );
  }

  shouldShowListMinPricesFields(): boolean {
    return this.showListMinPrices;
  }

  getPrimaryPriceLabel(): string {
    return 'Valor';
  }

  getClosePricingButtonLabel(): string {
    return this.isExpectedPriceMode() ? 'Finalizar e arquivar' : 'Enviar para negociação';
  }

  onClickClosePricing() {
    this.closeTutorial();

    if (this.showPricingTypeSelector() && this.pricingType.invalid) {
      this.pricingType.markAsTouched();
      return;
    }

    if (this.isExpectedPriceMode()) {
      this.archiveOnlineEvaluation();
      return;
    }

    // PURCHASE mode - save prices before closing
    this.closePricingWithSave();
  }

  private archiveOnlineEvaluation(): void {
    const req = this.buildUpdatePriceRequest();
    if (!req) {
      return;
    }

    this.loading = true;
    this.evaluationService
      .updatePrice(req)
      .pipe(
        switchMap((response: any) => {
          if (!response?.success) {
            throw new Error('UPDATE_PRICE_FAILED');
          }

          this.syncOldPriceValues(req);
          this.updatePrice.emit(req);
          return this.evaluationService.archive(this.vehicle.id, this.onlineArchiveReason);
        }),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: () => {
          this.toastrService.success(
            'Avaliação arquivada com sucesso.',
            null,
            {
              progressBar: true,
            }
          );
          const changeStatus: ChangeStatusEvaluation = {
            status: StatusEvaluation.ARCHIVED,
            itemHistory: {
              type: HistoryItemType.ARCHIVED,
              createdAt: new Date(),
            },
            reasonArchive: this.onlineArchiveReason,
          };
          this.changeStatus.emit(changeStatus);
          this.historyLocationService.goBackEvaluation();
        },
        error: () => {
          this.toastrService.error(
            'Falha ao arquivar a avaliação. Tente novamente mais tarde.'
          );
        },
      });
  }

  private closePricingWithSave(): void {
    const req = this.buildUpdatePriceRequest();
    if (!req) {
      return;
    }

    const addComment = this.addComment.value;
    const comments = addComment ? this.commentClosePricing.value : '';
    const id = this.vehicle.id;

    this.loading = true;
    this.evaluationService
      .updatePrice(req)
      .pipe(
        switchMap((response: any) => {
          if (!response?.success) {
            throw new Error('UPDATE_PRICE_FAILED');
          }

          this.syncOldPriceValues(req);
          this.updatePrice.emit(req);
          return this.evaluationService.closePricing(id, comments);
        }),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (response: any) => {
          if (response.success) {
            this.toastrService.success(
              'Enviado para negociação com sucesso',
              null,
              {
                progressBar: true,
              }
            );
            const changeStatus: ChangeStatusEvaluation = {
              status: StatusEvaluation.NEGOTIATION,
              itemHistory: {
                type: HistoryItemType.CLOSED_PRICING,
                createdAt: new Date(),
              },
            };
            this.changeStatus.emit(changeStatus);
            this.historyLocationService.goBackEvaluation();
          }
        },
        error: () => {
          this.toastrService.error(
            'Falha ao enviar a captação para negociação. Tente novamente mais tarde.'
          );
        },
      });
  }

  isOKFinalScore(): boolean {
    return this.vehicle.finalEvalScore >= 8;
  }
  isWarningFinalScore(): boolean {
    return this.vehicle.finalEvalScore > 5 && this.vehicle.finalEvalScore < 8;
  }
  isDangerFinalScore(): boolean {
    return this.vehicle.finalEvalScore <= 5;
  }

  closeObsModal() {
    this.obsModal = false;
    this.obsMsg = '';
  }

  sendObs() {
    this.loadingBtn = true;
    if (this.obsMsg != '') {
      this.evaluationService.sendPricingComment(this.vehicle.id, this.obsMsg).subscribe((resp: any) => {
        if (resp.success) {
          this.toastrService.success('Comentário enviado');
          this.vehicle = resp.vehicleDetails;
          this.closeObsModal();
        }
      });
    }
  }

  canAddAndViewObservations(): boolean {
    const hasLegacyPermission = this.userInfo?.hasRole(Role.DESK_BUY) || this.userInfo?.hasRole(Role.DESK_SELL) || this.userInfo?.hasRole(Role.OEM_DEALER_MANAGER);
    return hasLegacyPermission || this.userInfo?.hasAnyPermission(Permission.VIEW_PRICING_COMMENTS);
  }

  canReturnToEvaluator(): boolean {
    const hasPermission = this.userInfo?.hasAnyPermissionOrLegacy(Permission.RETURN_TO_EVALUATOR);
    return hasPermission;
  }

  private configurePricingTypeControl(): void {
    const shouldRequireSelection = this.isOnlineEvaluation() && this.canEditPricingCard;

    if (shouldRequireSelection) {
      this.pricingType.setValidators([Validators.required]);
      if (this.pricingType.value !== PricingFlowType.EXPECTED_PRICE
        && this.pricingType.value !== PricingFlowType.PURCHASE) {
        this.pricingType.setValue(null, { emitEvent: false });
      }
    } else {
      this.pricingType.clearValidators();
      this.pricingType.setValue(PricingFlowType.PURCHASE, { emitEvent: false });
      this.pricingType.markAsUntouched();
    }

    this.pricingType.updateValueAndValidity({ emitEvent: false });
  }

  private buildUpdatePriceRequest(): UpdateEvalPriceRequest | null {
    if (
      !this.vehicle ||
      !this.isPriceReady() ||
      !this.destination.valid ||
      !this.minSellPrice.valid ||
      !this.listPrice.valid
    ) {
      this.price.markAsTouched();
      this.minSellPrice.markAsTouched();
      this.listPrice.markAsTouched();

      if (this.isPriceAboveCalculatorCeiling(this.price.value)) {
        this.toastrService.warning(
          `O valor não pode ultrapassar ${this.calculatorPriceCeilingLabel()}.`,
        );
      } else {
        this.toastrService.warning('Por favor, preencha todos os campos obrigatórios da precificação.');
      }

      return null;
    }

    if (this.isCalculatorPriceLocked() && (this.tradeInMarketPrice == null || this.tradeInMarketPrice <= 0)) {
      this.toastrService.warning('Preencha a calculadora de precificação para definir o valor.');
      return null;
    }

    const req = new UpdateEvalPriceRequest();
    req.price = this.price.value;
    req.priceCent = toPriceCentValue(this.price.value) ?? undefined;
    req.id = this.vehicle.id;
    req.destination = this.destination.value;

    if (this.canEditFactoryBonus) {
      req.factoryBonus = this.factoryBonus.value;
    }

    if (this.canEditListMinPrices) {
      req.listPrice = this.listPrice.value;
      req.minSellPrice = this.minSellPrice.value;
    }

    if (this.isExpectedPriceMode()) {
      req.estimatedPrice = this.isExpectedPriceMode();
    }

    if (this.tradeInMarketPrice != null) {
      req.tradeInMarketPrice = this.tradeInMarketPrice;
      req.tradeInDealerCosts = this.tradeInDealerCosts ?? 0;
    }

    if (this.shouldShowEvaluatorField() && this.evaluator.value) {
      req.evaluatorId = this.evaluator.value;
    }

    return req;
  }

  private loadEvaluatorOptions(): void {
    if (!this.isOnlineEvaluation() || !this.canEditPricingCard) {
      return;
    }

    this.evaluationService.getEvaluatorsMyEvalDealer(this.vehicle.id)
      .subscribe((options) => {
        this.evaluatorOptions = options ?? [];
      });
  }

  private syncOldPriceValues(req: UpdateEvalPriceRequest): void {
    this.oldPrice = req.price;
    this.oldFactoryBonus = req.factoryBonus ?? null;
    this.oldMinSellPrice = req.minSellPrice ?? null;
    this.oldListPrice = req.listPrice ?? null;
  }
}